import { useEffect, useRef, useState } from "react";
import SelloAnimado from "../SelloAnimado";
import { IDENTIDAD_MIPAE } from "../../constants/identidadMiPAE";
import { MOMENTOS, VALORES_EN_MIPAE, tramosMision } from "../../lib/presentacionMiPAE";

// Piezas de la identidad aprobada (docs/identidad-mipae.md) para la
// presentación pública. Regla: los textos de propósito, misión, visión y
// valores se leen de constants/identidadMiPAE.js y se muestran LITERALES
// (test en presentacion/__tests__/QueEsMiPAE.test.jsx). Lo que se agrega
// alrededor (subrayados, "En MiPAE", la nota de aspiración) va separado a la
// vista y nunca reemplaza una palabra del texto aprobado.
//
// Pasada "menos Claude" (2026-09-29, pedido del fundador): sin rótulos en
// mayúsculas, sin cursivas, sin franja oscura ni fondos crema, sin números
// ni píldoras. Los títulos de sección van en la letra de la app (Inter, como
// Heading h2/h3) y la serif queda solo para el título de cada página.
//
// Pasada "con aire" (2026-09-29, el fundador en su teléfono: "se ve
// desproporcionado, le quita aire a la pantalla"): en el teléfono los títulos
// y los textos grandes bajan a la escala de la app (h2 = 20 px, body = 15 px)
// y el tamaño de vitrina queda para pantallas anchas.

export const TITULO_SECCION = "font-sans text-22px font-semibold leading-tight tracking-tight text-ink sm:text-28px";
// Bajo lg, títulos de sección y declaraciones van centrados: en un teléfono
// más ancho que 390 px el texto cargado a la izquierda se veía "fuera de
// centro" (fundador, 29-sep). En escritorio, a la izquierda como siempre.
export const CENTRADO = "mx-auto text-center lg:mx-0 lg:text-left";
// Texto destacado (propósito, misión, visión): legible sin gritar en el
// teléfono.
const TEXTO_DESTACADO = "text-18px font-medium text-ink sm:text-24px";
// Texto secundario con contraste AA en claro y en oscuro (en oscuro
// ink-subtle queda bajo 4,5:1 sobre el papel; ink-muted no).
export const TEXTO_2 = "text-ink-subtle dark:text-ink-muted";

// Subrayado del tramo de la misión con el color del momento que lo hace
// concreto. Clases enteras para que Tailwind las genere.
const SUBRAYADO = {
  aprender: "decoration-module-enciclopedia",
  fundamentar: "decoration-module-pae-rapido",
  turno: "decoration-module-prepara-turno",
};

// Color del guion de la leyenda (mismo módulo que el subrayado).
const GUION = {
  aprender: "text-module-enciclopedia",
  fundamentar: "text-module-pae-rapido",
  turno: "text-module-prepara-turno",
  consultar: "text-module-escalas",
};

// ─── Propósito ───────────────────────────────────────────────────────────────
export function BloqueProposito({ children, idTitulo = "proposito-titulo", titulo = "Por qué existe MiPAE" }) {
  return (
    <section aria-labelledby={idTitulo} className="border-t border-edge bg-paper">
      <div className="mx-auto max-w-[1240px] px-5 py-14 sm:py-16 lg:py-20">
        <div className={`pp-aparece max-w-[50rem] ${CENTRADO}`}>
          <h2 id={idTitulo} className={TITULO_SECCION}>{titulo}</h2>
          <p className={`mt-4 leading-snug ${TEXTO_DESTACADO}`}>
            {IDENTIDAD_MIPAE.proposito}
          </p>
          {children}
        </div>
      </div>
    </section>
  );
}

// ─── Misión ──────────────────────────────────────────────────────────────────
// La misión literal con sus tres verbos subrayados en el color del momento
// que los hace concretos, y al lado el puente a esos momentos.
export function MisionConectada({ onIrMomento }) {
  const tramos = tramosMision(IDENTIDAD_MIPAE.mision);
  const nombreMomento = (id) => MOMENTOS.find((m) => m.id === id)?.pestana;
  const enlaces = [
    { tramo: "aprender", momentos: ["aprender"] },
    { tramo: "fundamentar sus decisiones", momentos: ["fundamentar"] },
    { tramo: "mejorar su práctica", momentos: ["turno", "consultar"] },
  ];
  return (
    <section aria-labelledby="mision-titulo" className="border-t border-edge bg-paper">
      <div className="mx-auto max-w-[1240px] px-5 py-14 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-16">
          <div className={`pp-aparece min-w-0 ${CENTRADO}`}>
            <h2 id="mision-titulo" className={TITULO_SECCION}>Nuestra misión</h2>
            <p className={`mt-4 leading-[1.55] ${TEXTO_DESTACADO}`}>
              {tramos.map((t, i) => (t.momento ? (
                <span key={i} className={`underline decoration-[3px] underline-offset-[6px] ${SUBRAYADO[t.momento] || ""}`}>{t.texto}</span>
              ) : (
                <span key={i}>{t.texto}</span>
              )))}
            </p>
          </div>
          <div className="pp-aparece mx-auto w-full min-w-0 max-w-[28rem] lg:mx-0 lg:max-w-none lg:pt-12" style={{ "--pp-retraso": "150ms" }}>
            <p className={`text-center text-body leading-relaxed lg:text-left ${TEXTO_2}`}>
              Lo subrayado se ve así en la app.
            </p>
            <ul className="mt-3 space-y-1">
              {enlaces.flatMap(({ tramo, momentos }) => momentos.map((id) => (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => onIrMomento?.(id)}
                    className="group flex min-h-[44px] w-full items-center gap-3 rounded-tk-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    <span aria-hidden="true" className={`h-[3px] w-6 flex-shrink-0 rounded-full bg-current ${GUION[id]}`} />
                    <span className="text-small">
                      <span className="font-semibold text-ink group-hover:text-accent dark:group-hover:text-accent-light">{nombreMomento(id)}</span>
                      <span className={TEXTO_2}> · {tramo}</span>
                    </span>
                  </button>
                </li>
              )))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Valores ─────────────────────────────────────────────────────────────────
// Lista simple de seis. La descripción aprobada va en tinta plena; "En MiPAE"
// va debajo, más chica, con su rótulo y una línea al costado, para que nadie
// confunda la prueba con el texto del valor.
export function ValoresMiPAE() {
  return (
    <section aria-labelledby="valores-titulo" className="border-t border-edge bg-paper">
      <div className="mx-auto max-w-[1240px] px-5 py-14 sm:py-16 lg:py-20">
        <div className={`pp-aparece max-w-[40rem] ${CENTRADO}`}>
          <h2 id="valores-titulo" className={TITULO_SECCION}>Nuestros valores</h2>
          <p className={`mt-3 text-body leading-relaxed ${TEXTO_2}`}>
            Cada uno con su texto completo y un ejemplo de cómo se ve hoy en la app.
          </p>
        </div>
        <ul className="mt-8 grid grid-cols-1 gap-x-14 gap-y-9 sm:mt-10 md:grid-cols-2 md:gap-y-10">
          {IDENTIDAD_MIPAE.valores.map((v, i) => (
            <li key={v.nombre} className="pp-aparece min-w-0" style={{ "--pp-retraso": `${(i % 2) * 90}ms` }}>
              <h3 className="text-h3 text-ink sm:text-18px">{v.nombre}</h3>
              <p className="mt-1.5 text-body leading-relaxed text-ink">{v.descripcion}</p>
              {VALORES_EN_MIPAE[v.nombre] && (
                <p className={`mt-3 border-l-2 border-edge pl-3 text-small font-normal leading-relaxed ${TEXTO_2}`}>
                  <span className="font-semibold text-ink">En MiPAE</span>{". "}
                  {VALORES_EN_MIPAE[v.nombre]}
                </p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ─── Visión ──────────────────────────────────────────────────────────────────
// La visión es una aspiración y se rotula así (docs/identidad-mipae.md): lo
// que la página muestra arriba es lo disponible hoy.
export function VisionAspiracion() {
  return (
    <section aria-labelledby="vision-titulo" className="border-t border-edge bg-paper">
      <div className="mx-auto max-w-[1240px] px-5 py-14 sm:py-16 lg:py-20">
        <div className={`pp-aparece max-w-[50rem] ${CENTRADO}`}>
          <h2 id="vision-titulo" className={TITULO_SECCION}>Nuestra visión</h2>
          <p className={`mt-4 leading-snug ${TEXTO_DESTACADO}`}>{IDENTIDAD_MIPAE.vision}</p>
          <p className={`mx-auto mt-5 max-w-[40rem] text-body leading-relaxed lg:mx-0 ${TEXTO_2}`}>
            Es a lo que apuntamos. Lo que muestra esta página es lo que MiPAE hace hoy, en español, en la web, en iPhone y en Android.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Sello del autor ─────────────────────────────────────────────────────────
// Se monta recién cuando entra en pantalla: si animara al cargar, terminaría
// antes de que alguien baje a verlo. Sin IntersectionObserver (tests,
// navegadores viejos) se monta al tiro. SelloAnimado ya respeta
// prefers-reduced-motion por dentro.
export function SelloAlLlegar({ size = 104 }) {
  const ref = useRef(null);
  const [visto, setVisto] = useState(() => typeof window === "undefined" || !("IntersectionObserver" in window));
  useEffect(() => {
    if (visto || !ref.current) return undefined;
    const obs = new IntersectionObserver((entradas) => {
      if (entradas.some((e) => e.isIntersecting)) { setVisto(true); obs.disconnect(); }
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [visto]);
  return (
    <div ref={ref} className="mx-auto" style={{ width: size, height: size }}>
      {visto && <SelloAnimado size={size} />}
    </div>
  );
}
